import { useCallback, useEffect, useMemo, useState } from "react";
import { Receipt, Search } from "lucide-react";
import { api, apiError, brl, dataHoraBR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, Loading, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";

export function FinanceSales({ period }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [origin, setOrigin] = useState("");
  const [payment, setPayment] = useState("");
  const [term, setTerm] = useState("");

  const load = useCallback(() => {
    setError("");
    api
      .get("/finance/sales", {
        params: {
          start: period.start,
          end: period.end,
          origin: origin || undefined,
          payment_status: payment || undefined,
        },
      })
      .then(({ data }) => setData(data))
      .catch((e) => setError(apiError(e)));
  }, [period.start, period.end, origin, payment]);

  useEffect(load, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = term.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      `${r.number} ${r.customer_name} ${r.items.map((i) => i.name).join(" ")}`
        .toLowerCase()
        .includes(q)
    );
  }, [data, term]);

  const origins = useMemo(
    () =>
      data
        ? [...new Map(data.rows.map((r) => [r.origin, r.origin_label])).entries()].map(
            ([value, label]) => ({ value, label })
          )
        : [],
    [data]
  );

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando vendas..." />;

  return (
    <div className="space-y-4" data-testid="finance-sales">
      <div className="grid gap-3 sm:grid-cols-3">
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Vendido</p>
          <p className="mt-1 font-num text-xl font-semibold" data-testid="sales-total-sold">
            {brl(data.sold)}
          </p>
        </Surface>
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Recebido no período
          </p>
          <p
            className="mt-1 font-num text-xl font-semibold text-emerald-300"
            data-testid="sales-total-received"
          >
            {brl(data.received_in_period)}
          </p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            pela data da confirmação do pagamento
          </p>
        </Surface>
        <Surface className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Pendente</p>
          <p
            className="mt-1 font-num text-xl font-semibold text-amber-300"
            data-testid="sales-total-pending"
          >
            {brl(data.pending)}
          </p>
        </Surface>
      </div>

      {(data.received_other_period > 0 ||
        data.received_without_date > 0 ||
        data.rows.some((r) => !r.sold_in_period)) && (
        <p className="text-[11px] text-muted-foreground" data-testid="sales-received-note">
          {data.received_other_period > 0 &&
            `${brl(data.received_other_period)} destas vendas foram recebidos em outra data (fora do período). `}
          {data.rows.some((r) => !r.sold_in_period) &&
            "Pedidos marcados como \"venda de outro período\" aparecem porque o pagamento foi confirmado dentro do período selecionado. "}
          {data.received_without_date > 0 &&
            `${brl(data.received_without_date)} estão marcados como pagos sem data de confirmação registrada (pedidos antigos) — não atribuímos uma data a eles.`}
        </p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar pedido, cliente ou produto..."
            className="pl-9"
            data-testid="sales-search"
          />
        </div>
        <div className="sm:w-44">
          <NativeSelect
            value={origin}
            onChange={setOrigin}
            placeholder="Todas as origens"
            options={origins}
            data-testid="sales-origin-filter"
          />
        </div>
        <div className="sm:w-48">
          <NativeSelect
            value={payment}
            onChange={setPayment}
            placeholder="Todos os pagamentos"
            options={[
              { value: "AGUARDANDO", label: "Aguardando" },
              { value: "CONFIRMADO", label: "Confirmado" },
            ]}
            data-testid="sales-payment-filter"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhuma venda encontrada"
          description="Ajuste o período ou os filtros para ver as vendas."
        />
      ) : (
        <Surface className="overflow-x-auto p-0" data-testid="sales-table">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="p-3">Pedido</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Produtos</th>
                <th className="p-3">Origem</th>
                <th className="p-3">Pagamento</th>
                <th className="p-3 text-right">Valor</th>
                <th className="p-3">Recebido em</th>
                <th className="p-3 text-right">Recebido</th>
                <th className="p-3 text-right">Pendente</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.order_id}
                  className="border-b border-white/5"
                  data-testid={`sale-row-${r.order_id}`}
                >
                  <td className="p-3">
                    <p className="font-num">{r.number}</p>
                    <p className="font-num text-[11px] text-muted-foreground">
                      {dataHoraBR(r.created_at)}
                    </p>
                    {!r.sold_in_period && (
                      <span className="text-[10px] text-brand">venda de outro período</span>
                    )}
                  </td>
                  <td className="p-3">{r.customer_name || "—"}</td>
                  <td className="max-w-[240px] p-3">
                    {r.items.map((i, idx) => (
                      <p key={idx} className="truncate text-xs">
                        {i.quantity}x {i.name}
                        {i.customization?.length > 0 && (
                          <span className="ml-1 text-brand">✨</span>
                        )}
                      </p>
                    ))}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{r.origin_label}</td>
                  <td className="p-3">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${
                        r.payment_status === "CONFIRMADO"
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                          : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                      }`}
                    >
                      {r.payment_status === "CONFIRMADO" ? "confirmado" : "aguardando"}
                    </span>
                    {r.payment_informed && r.payment_status !== "CONFIRMADO" && (
                      <p className="mt-1 text-[10px] text-muted-foreground">cliente informou</p>
                    )}
                    {r.payment_method && (
                      <p className="mt-1 text-[10px] text-muted-foreground">{r.payment_method}</p>
                    )}
                  </td>
                  <td className="p-3 text-right font-num">{brl(r.total)}</td>
                  <td className="p-3 text-xs" data-testid={`sale-paid-at-${r.order_id}`}>
                    {r.paid_at ? (
                      <span className={r.received_in_period ? "" : "text-muted-foreground"}>
                        {dataHoraBR(r.paid_at)}
                        {!r.received_in_period && (
                          <span className="block text-[10px]">fora do período</span>
                        )}
                      </span>
                    ) : r.payment_date_missing ? (
                      <span className="text-amber-300">sem data de confirmação</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-num text-emerald-300">{brl(r.received)}</td>
                  <td className="p-3 text-right font-num text-amber-300">{brl(r.pending)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      )}
    </div>
  );
}
